const mobileSearchIcon = document.querySelector('#mobile-search-icon');
const desktopSearchIcon = document.querySelector('#desktop-search-icon');
const searchBar = document.querySelector('#search-bar');
const searchInput = document.querySelector('#search-input');
const searchCloseButton = document.querySelector('#search-close-button');
const searchResults = document.querySelector('#search-results');
const searchOverlay = document.querySelector('#search-overlay');

// Initialize debounce timer
let debounceTimer;

// EXCLUDE PAGES AND COLLECTIONS FROM SEARCH RESULTS
// Add page handles or collection handles that should not appear in search results
const excludedFromSearch = [
  "pages/welcome-new-customer",
];

// Price formatting helper to consistently add currency and decimals
function formatPrice(rawPrice) {
  if (rawPrice === null || rawPrice === undefined || rawPrice === '') return '';

  let numericCents;

  if (typeof rawPrice === 'string') {
    const cleaned = rawPrice.replace(/[^\d.,-]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    if (Number.isNaN(parsed)) return '';
    numericCents = Math.round(parsed * 100);
  } else if (typeof rawPrice === 'number') {
    numericCents = Number.isInteger(rawPrice) && rawPrice > 20
      ? rawPrice
      : Math.round(rawPrice * 100);
  } else {
    return '';
  }

  return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(numericCents / 100);
}

// Store and retrieve recent searches
function getRecentSearches() {
  try {
    const recentSearches = localStorage.getItem('recentSearches');
    return recentSearches ? JSON.parse(recentSearches) : [];
  } catch (e) {
    console.error('Error retrieving recent searches:', e);
    return [];
  }
}

// Normalize URL to ensure consistent format for comparison
function normalizeUrl(url) {
  if (!url) return '';
  return url.replace(/\/$/, '').toLowerCase();
}

// Normalize text for search comparison - removes hyphens, spaces, and makes lowercase
function normalizeSearchText(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/-/g, '')
    .replace(/\s+/g, '');
}

function addToRecentSearches(item) {
  try {
    const recentSearches = getRecentSearches();
    const normalizedItemUrl = normalizeUrl(item.url);
    const existingIndex = recentSearches.findIndex(search => normalizeUrl(search.url) === normalizedItemUrl);
    
    if (existingIndex !== -1) {
      recentSearches.splice(existingIndex, 1);
    }
    
    recentSearches.unshift(item);
    const limitedSearches = recentSearches.slice(0, 5);
    localStorage.setItem('recentSearches', JSON.stringify(limitedSearches));
  } catch (e) {
    console.error('Error saving recent searches:', e);
  }
}

// Normalize product data helper function
function normalizeProduct(product) {
  let handle = product.handle;
  if (!handle && product.url) {
    const urlMatch = product.url.match(/\/products\/([^\/\?]+)/);
    handle = urlMatch ? urlMatch[1] : null;
  }
  
  const normalizedUrl = handle ? `/products/${handle}` : normalizeUrl(product.url);
  
  let image = product.image || product.featured_image;
  if (!image && product.images && product.images.length > 0) {
    image = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
  }
  
  const rawVariantPrice = product.variants && product.variants.length > 0 ? product.variants[0].price : null;
  const rawPrice = product.price !== undefined && product.price !== null ? product.price : rawVariantPrice;
  const price = product.price_formatted && product.price_formatted.trim() !== ''
    ? product.price_formatted
    : formatPrice(rawPrice);
  
  return {
    ...product,
    handle: handle || product.handle,
    url: normalizedUrl,
    image: image,
    price: price
  };
}

// Handle search functionality using Shopify Predictive Search API
async function performSearch(query) {
  // Clear results if query is empty or less than 2 characters
  if (!query || query.length < 2) {
    searchResults.style.display = 'none';
    searchResults.innerHTML = '';
    return;
  }

  // Show search results container
  searchResults.style.display = 'block';
  searchResults.innerHTML = '<div class="search-loading">Suchen...</div>';

  try {
    // Use Shopify Predictive Search API with SKU and body search enabled
    // Default fields: title, product_type, variants.title, vendor
    // Additional fields: variants.sku (for SKU search), body (for product description search)
    // Request 15 products to account for potential duplicates, then limit to 10 unique products
    const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,collection&resources[limit]=15&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,body&resources[options][unavailable_products]=show`;

    const response = await fetch(searchUrl);
    const data = await response.json();

    let resultsHTML = '';
    
    // Get recent searches for display
    const recentSearches = getRecentSearches();
    const seenUrls = new Set();
    const uniqueRecentSearches = recentSearches.filter(item => {
      const url = normalizeUrl(item.url);
      return url && !seenUrls.has(url) && seenUrls.add(url);
    });
    
    const hasRecentSearches = uniqueRecentSearches.length > 0;
    
    // Create a container div with two columns
    resultsHTML = '<div class="search-results-container">';
    
    // Add recent searches column if we have any
    if (hasRecentSearches) {
      resultsHTML += '<div class="search-results-column recent-searches">';
      resultsHTML += '<div class="search-results-group"><h3>Letzte Suchen</h3><ul>';
      
      uniqueRecentSearches.forEach(item => {
        const itemImage = item.image ? `<img src="${item.image}" alt="${item.title}" class="search-result-image">` : '';
        const price = item.price ? `<span class="search-result-price">${item.price}</span>` : '';
        const sku = item.sku ? `<span class="search-result-sku">SKU: ${item.sku}</span>` : '';
        const type = item.type ? `<span class="search-result-type">${item.type}</span>` : '';
        
        resultsHTML += `
          <li class="search-result-item">
            <a href="${item.url}" class="search-result-link">
              ${itemImage}
              <div class="search-result-details">
                <span class="search-result-title">${item.title}</span>
                ${type}
                ${sku}
                ${price}
              </div>
            </a>
          </li>
        `;
      });
      
      resultsHTML += '</ul></div>';
      resultsHTML += '</div>';
    }
    
    // Add current search results column
    resultsHTML += `<div class="search-results-column current-search${!hasRecentSearches ? ' full-width' : ''}">`;
    
    // Process product results
    let hasCurrentResults = false;
    const productMap = new Map();
    const normalizedQuery = normalizeSearchText(query);
    
    // Process products from Predictive Search API
    // The API already searches in title, product_type, variants.title, vendor, variants.sku, and body
    if (data.resources?.results?.products && data.resources.results.products.length > 0) {
      // Process all products from API response (don't break early)
      for (const product of data.resources.results.products) {
        const normalized = normalizeProduct(product);
        const key = normalized.handle || normalizeUrl(normalized.url);
        
        // Only add if it's a unique product (not already in map)
        if (key && !productMap.has(key)) {
          // Check for SKU matches to highlight in results
          let matchingSku = '';
          let exactSkuMatch = false;
          if (product.variants?.length > 0) {
            const matchingVariants = product.variants.filter(v => {
              if (!v.sku) return false;
              const normalizedSku = normalizeSearchText(v.sku);
              return normalizedSku.includes(normalizedQuery) || normalizedQuery.includes(normalizedSku);
            });
            
            if (matchingVariants.length > 0) {
              matchingSku = matchingVariants[0].sku;
              exactSkuMatch = matchingVariants.some(v => {
                const normalizedSku = normalizeSearchText(v.sku);
                return normalizedSku === normalizedQuery;
              });
              
              if (matchingVariants.length > 1) {
                matchingSku += ` (+${matchingVariants.length - 1} weitere)`;
              }
            }
          }
          
          normalized._matchingSku = matchingSku;
          normalized._isExactSkuMatch = exactSkuMatch;
          productMap.set(key, normalized);
        }
      }
    }
    
    // Convert map to array, prioritize exact SKU matches
    const allProducts = Array.from(productMap.values())
      .sort((a, b) => {
        // Prioritize exact SKU matches
        if (a._isExactSkuMatch && !b._isExactSkuMatch) return -1;
        if (!a._isExactSkuMatch && b._isExactSkuMatch) return 1;
        return 0;
      })
      .slice(0, 10);
    
    // Display products
    if (allProducts.length > 0) {
      hasCurrentResults = true;
      resultsHTML += '<div class="search-results-group"><h3>Produkte</h3><ul>';
      
      allProducts.forEach(product => {
        const matchingSku = product._matchingSku || '';
        const productImage = product.image ? `<img src="${product.image}" alt="${product.title}" class="search-result-image">` : '';
        const price = product.price ? `<span class="search-result-price">${product.price}</span>` : '';
        const sku = matchingSku ? `<span class="search-result-sku">SKU: ${matchingSku}</span>` : '';
        
        // Save product to potentially add to recent searches when clicked
        const productData = {
          url: product.url,
          title: product.title,
          image: product.image || '',
          price: product.price || '',
          sku: matchingSku || '',
          type: 'Produkt'
        };
        
        resultsHTML += `
          <li class="search-result-item">
            <a href="${product.url}" 
               class="search-result-link" 
               data-item='${JSON.stringify(productData).replace(/'/g, "&apos;")}'>
              ${productImage}
              <div class="search-result-details">
                <span class="search-result-title">${product.title}</span>
                ${sku}
                ${price}
              </div>
            </a>
          </li>
        `;
      });
      resultsHTML += '</ul></div>';
    }
    
    // Process collection results
    if (data.resources?.results?.collections && data.resources.results.collections.length > 0) {
      const filteredCollections = data.resources.results.collections.filter(collection => {
        return !excludedFromSearch.some(excluded => collection.url.includes(excluded));
      });
      
      if (filteredCollections.length > 0) {
        hasCurrentResults = true;
        resultsHTML += '<div class="search-results-group"><h3>Kollektionen</h3><ul>';
        filteredCollections.forEach(collection => {
          const collectionImage = collection.image ? `<img src="${collection.image}" alt="${collection.title}" class="search-result-image">` : '';
          
          const collectionData = {
            url: collection.url,
            title: collection.title,
            image: collection.image || '',
            type: 'Kollektion'
          };
          
          resultsHTML += `
            <li class="search-result-item">
              <a href="${collection.url}" 
                 class="search-result-link" 
                 data-item='${JSON.stringify(collectionData).replace(/'/g, "&apos;")}'>
                ${collectionImage}
                <div class="search-result-details">
                  <span class="search-result-title">${collection.title}</span>
                </div>
              </a>
            </li>
          `;
        });
        resultsHTML += '</ul></div>';
      }
    }

    // Process page results
    if (data.resources?.results?.pages && data.resources.results.pages.length > 0) {
      const filteredPages = data.resources.results.pages.filter(page => {
        return !excludedFromSearch.some(excluded => page.url.includes(excluded));
      });
      
      if (filteredPages.length > 0) {
        hasCurrentResults = true;
        resultsHTML += '<div class="search-results-group"><h3>Seiten</h3><ul>';
        filteredPages.forEach(page => {
          const pageData = {
            url: page.url,
            title: page.title,
            type: 'Seite'
          };
          
          resultsHTML += `
            <li class="search-result-item">
              <a href="${page.url}" 
                 class="search-result-link" 
                 data-item='${JSON.stringify(pageData).replace(/'/g, "&apos;")}'>
                <span class="search-result-title">${page.title}</span>
              </a>
            </li>
          `;
        });
        resultsHTML += '</ul></div>';
      }
    }
    
    // Show no results message if nothing found
    if (!hasCurrentResults) {
      resultsHTML += '<div class="search-no-results">Keine Ergebnisse gefunden</div>';
    }
    
    resultsHTML += '</div>'; // End current search column
    
    // Add view all results link
    resultsHTML += `<div class="search-view-all"><a href="/search?q=${encodeURIComponent(query)}" aria-label="Alle Produkte anzeigen">Alle Produkte anzeigen</a></div>`;
    
    resultsHTML += '</div>'; // End container
    
    searchResults.innerHTML = resultsHTML;
    
    // Add click handlers for all search result links to save to recent searches
    document.querySelectorAll('.search-result-link[data-item]').forEach(link => {
      link.addEventListener('click', function() {
        try {
          const itemData = JSON.parse(this.getAttribute('data-item'));
          addToRecentSearches(itemData);
        } catch (e) {
          console.error('Error processing item data', e);
        }
      });
    });
  } catch (error) {
    console.error('Search error:', error);
    searchResults.innerHTML = '<div class="search-error">Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.</div>';
  }
}

// Event listener for search input with debounce
searchInput.addEventListener('input', function() {
  const query = this.value.trim();
  
  // Hide results if query is empty
  if (!query) {
    searchResults.style.display = 'none';
    searchResults.innerHTML = '';
    return;
  }

  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    performSearch(query);
  }, 300); // 300ms debounce
});

// Handle search submission (redirect to search page)
function handleSearchSubmit() {
  const query = searchInput.value.trim();
  if (query) {
    window.location.href = `/search?q=${encodeURIComponent(query)}`;
  }
}

// Handle search close button click (now acts as search submit)
searchCloseButton.addEventListener('click', function(e) {
  e.preventDefault();
  handleSearchSubmit();
});

// Handle Enter key press on search input
searchInput.addEventListener('keypress', function(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    handleSearchSubmit();
  }
});

// Close search when clicking outside
window.addEventListener('click', function(e) {
  if (mobileSearchIcon && !mobileSearchIcon.contains(e.target) && 
      desktopSearchIcon && !desktopSearchIcon.contains(e.target) && 
      searchBar && !searchBar.contains(e.target)) {
    searchBar.classList.remove('active');
    if (searchOverlay) searchOverlay.classList.remove('active');
    document.body.classList.remove('no-scroll');
  }
});

// Event listener for mobile search icon
if (mobileSearchIcon) {
  mobileSearchIcon.addEventListener('click', function() {
    document.body.classList.add('no-scroll');
    searchBar.classList.add('active');
    if (searchOverlay) searchOverlay.classList.add('active');
    searchInput.focus();
  });
}

// Event listener for desktop search icon
if (desktopSearchIcon) {
  desktopSearchIcon.addEventListener('click', function() {
    document.body.classList.add('no-scroll');
    searchBar.classList.add('active');
    if (searchOverlay) searchOverlay.classList.add('active');
    searchInput.focus();
  });
}
